/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright (c) 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { Text, NativeCheckbox, NativeInput } from '@/shared/ui';
import { clampAdBudgetReais, isBelowMinimumAdBudget, minAdDailyBudgetReais } from '@/shared/utils/ad-dialog-helpers';
import type { MissingAdSetting } from '@/shared/utils/ad-readiness';
import { AdSetupNotice } from '../ad-dialogs/AdSetupNotice';
import { STATUS_LABELS } from './constants';
import { scheduleDatetimeLocalMin } from './publish-helpers';

type T = (key: string, opts?: Record<string, unknown>) => string;

interface PortalRowProps {
  p: any;
  t: T;
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  results: Record<string, any> | null;
  savedStatus: Record<string, string>;
  hasImages: boolean;
  igAccountId?: string | null;
}

/**
 * Resolves the portal row description, preferring Instagram link/image warnings.
 */
function portalDescription(
  t: T,
  p: { desc: string },
  igNotLinked: boolean,
  needsImages: boolean,
): string {
  if (igNotLinked) return t('publish.instagramNotLinked');
  if (needsImages) return t('publish.instagramRequiresImages');
  return p.desc;
}

const ACTIVE_SAVED = new Set(['published', 'queued', 'scheduled']);

/**
 * Selectable portal row: label + status on the first line, helper/warning text
 * below. Warning states (missing images / IG not linked) stay fully opaque so
 * the orange copy remains readable; only generic unavailable rows are dimmed.
 */
export function PortalRow({ p, t, selected, setSelected, results, savedStatus, hasImages, igAccountId }: PortalRowProps) {
  const backendId = (p as { backendId?: string }).backendId ?? p.id;
  const currentResult = results?.[backendId];
  const statusKey = currentResult
    ? (currentResult.status === 'error' ? currentResult.reason : currentResult.status)
    : null;
  const statusInfo = statusKey ? STATUS_LABELS[statusKey] : null;
  const isInstagram = p.id === 'instagram';
  const igNotLinked = isInstagram && !igAccountId;
  const needsImages = isInstagram && !hasImages;
  const isDisabled = Boolean((p as { unavailable?: boolean }).unavailable) || needsImages || igNotLinked;
  const desc = portalDescription(t, p, igNotLinked, needsImages);
  const warnDesc = igNotLinked || needsImages;

  const savedPortalStatus = savedStatus[backendId] ?? savedStatus[p.id];
  const isAlreadyPublished = !isDisabled && ACTIVE_SAVED.has(savedPortalStatus);
  const dimUnavailable = isDisabled && !warnDesc;

  const checkboxOpacity = dimUnavailable ? 'opacity-50' : '';
  const labelColor = warnDesc ? 'text-[var(--text-secondary)]' : 'text-[var(--text-primary)]';
  const descColor = warnDesc ? 'text-[var(--status-warning-text)]' : 'text-[var(--text-tertiary)]';
  const dimClass = dimUnavailable ? 'opacity-50' : '';

  return (
    <Text as="label" className={`flex items-start gap-(--space-2) ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
      <NativeCheckbox
        checked={!isDisabled && selected.includes(p.id)}
        disabled={isDisabled}
        onChange={() => {
          if (isDisabled) return;
          setSelected((prev) => prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id]);
        }}
        className={`w-3.5 h-3.5 mt-(--space-0-5) accent-[var(--seed-base)] ${checkboxOpacity}`}
        aria-label={p.label}
        data-testid={`portal-checkbox-${p.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-(--space-2) flex-wrap">
          <Text as="span" className={`text-xs font-medium ${labelColor} ${dimClass}`}>
            {p.label}
          </Text>
          {isAlreadyPublished && (
            <Text as="span" className="text-xs font-medium text-[var(--status-success-text)]">
              {t('publish.status.published')}
            </Text>
          )}
          {!isDisabled && !isAlreadyPublished && statusInfo && (
            <Text as="span" className={`text-xs font-medium ${statusInfo.color}`}>{t(statusInfo.textKey)}</Text>
          )}
        </div>
        <Text
          as="span"
          className={`text-xs block ${descColor} ${dimClass}`}
          {...(needsImages ? { 'data-testid': 'instagram-requires-images-hint' } : {})}
          {...(igNotLinked ? { 'data-testid': 'instagram-not-linked-hint' } : {})}
        >
          {desc}
        </Text>
      </div>
    </Text>
  );
}

interface LeadAdPanelProps {
  t: T;
  /** i18n keys, e.g. 'publish.fbCreateLeadAd' / 'publish.igCreateLeadAd'. */
  labelKey: string;
  requiresCardKey: string;
  hasPaymentMethod: boolean | null;
  createLeadAd: boolean;
  setCreateLeadAd: (v: boolean) => void;
  leadAdBudget: number;
  setLeadAdBudget: (v: number) => void;
  /** What the agency must still fill in before it can create a Lead Ad; the panel then only says so. */
  missing: MissingAdSetting[];
  settingsHref: string;
}

/**
 * Optional lead-ad panel shown inside the publish dialog for a given
 * platform. Lets the broker opt into creating a lead ad and set a daily
 * budget, or explains that a payment method is required when none is
 * configured. Facebook and Instagram lead ads both bill through the same
 * Facebook ad account, so `hasPaymentMethod` is the one shared flag driving
 * both instances of this panel.
 */
function LeadAdPanel({
  t, labelKey, requiresCardKey, hasPaymentMethod, createLeadAd, setCreateLeadAd, leadAdBudget, setLeadAdBudget,
  missing, settingsHref,
}: LeadAdPanelProps) {
  return (
    <div className="p-(--space-3) rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-secondary)] space-y-(--space-2)">
      {missing.length > 0 ? (
        <div className="flex items-start gap-(--space-2)">
          <NativeCheckbox disabled className="w-3.5 h-3.5 mt-(--space-0-5) opacity-40 cursor-not-allowed" aria-label={t(labelKey)} />
          <div className="flex flex-col gap-(--space-1-5)">
            <Text as="span" className="text-xs font-medium opacity-40 text-[var(--text-primary)]">{t(labelKey)}</Text>
            <AdSetupNotice t={t} kind="leadAd" missing={missing} settingsHref={settingsHref} testId={`${labelKey}-setup`} />
          </div>
        </div>
      ) : hasPaymentMethod === false ? (
        <div className="flex items-start gap-(--space-2)">
          <NativeCheckbox disabled className="w-3.5 h-3.5 mt-(--space-0-5) opacity-40 cursor-not-allowed" aria-label={t(labelKey)} />
          <div>
            <Text as="span" className="text-xs font-medium opacity-40 text-[var(--text-primary)]">{t(labelKey)}</Text>
            <Text className="m-0 mt-(--space-0-5) text-xs text-[var(--status-warning-text)]">{t(requiresCardKey)}</Text>
          </div>
        </div>
      ) : (
        <Text as="label" className="flex items-center gap-(--space-2) cursor-pointer">
          <NativeCheckbox checked={createLeadAd} onChange={() => setCreateLeadAd(!createLeadAd)} className="w-3.5 h-3.5 accent-[var(--seed-base)]" />
          <Text as="span" className="text-xs font-medium text-[var(--text-primary)]">{t(labelKey)}</Text>
        </Text>
      )}
      {createLeadAd && hasPaymentMethod !== false && (
        <div className="flex items-center gap-(--space-2) pl-(--space-5)">
          <Text as="span" className="text-xs text-[var(--text-tertiary)]">R$</Text>
          <NativeInput
            type="number"
            min={minAdDailyBudgetReais()}
            value={leadAdBudget}
            onChange={(e) => setLeadAdBudget(Number(e.target.value))}
            onBlur={() => setLeadAdBudget(clampAdBudgetReais(leadAdBudget))}
            className="w-16 text-xs rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-secondary)] px-(--space-2) py-(--space-1) text-[var(--text-primary)]"
          />
          <Text as="span" className="text-xs text-[var(--text-tertiary)]">/dia</Text>
        </div>
      )}
      {createLeadAd && hasPaymentMethod !== false && isBelowMinimumAdBudget(leadAdBudget) && (
        <Text className="m-0 pl-(--space-5) text-xs text-[var(--status-warning-text)]" data-testid="lead-ad-budget-min-warning">
          {t('publish.leadAdBudgetMin', { min: minAdDailyBudgetReais() })}
        </Text>
      )}
    </div>
  );
}

interface FacebookLeadAdPanelProps {
  t: T;
  fbHasPaymentMethod: boolean | null;
  fbCreateLeadAd: boolean;
  setFbCreateLeadAd: (v: boolean) => void;
  fbLeadAdBudget: number;
  setFbLeadAdBudget: (v: number) => void;
  missing: MissingAdSetting[];
  settingsHref: string;
}

/** Facebook lead-ad panel -- see {@link LeadAdPanel}. */
export function FacebookLeadAdPanel({
  t, fbHasPaymentMethod, fbCreateLeadAd, setFbCreateLeadAd, fbLeadAdBudget, setFbLeadAdBudget, missing, settingsHref,
}: FacebookLeadAdPanelProps) {
  return (
    <LeadAdPanel
      t={t}
      labelKey="publish.fbCreateLeadAd"
      requiresCardKey="publish.fbLeadAdRequiresCard"
      hasPaymentMethod={fbHasPaymentMethod}
      createLeadAd={fbCreateLeadAd}
      setCreateLeadAd={setFbCreateLeadAd}
      leadAdBudget={fbLeadAdBudget}
      setLeadAdBudget={setFbLeadAdBudget}
      missing={missing}
      settingsHref={settingsHref}
    />
  );
}

interface InstagramLeadAdPanelProps {
  t: T;
  igHasPaymentMethod: boolean | null;
  igCreateLeadAd: boolean;
  setIgCreateLeadAd: (v: boolean) => void;
  igLeadAdBudget: number;
  setIgLeadAdBudget: (v: number) => void;
  missing: MissingAdSetting[];
  settingsHref: string;
}

/**
 * Instagram lead-ad panel -- see {@link LeadAdPanel}. `igHasPaymentMethod`
 * is really the same Facebook ad account's payment status (Instagram ads
 * have no ad account of their own), passed in under this name so the two
 * panels read the same way at their call site.
 */
export function InstagramLeadAdPanel({
  t, igHasPaymentMethod, igCreateLeadAd, setIgCreateLeadAd, igLeadAdBudget, setIgLeadAdBudget, missing, settingsHref,
}: InstagramLeadAdPanelProps) {
  return (
    <LeadAdPanel
      t={t}
      labelKey="publish.igCreateLeadAd"
      requiresCardKey="publish.igLeadAdRequiresCard"
      hasPaymentMethod={igHasPaymentMethod}
      createLeadAd={igCreateLeadAd}
      setCreateLeadAd={setIgCreateLeadAd}
      leadAdBudget={igLeadAdBudget}
      setLeadAdBudget={setIgLeadAdBudget}
      missing={missing}
      settingsHref={settingsHref}
    />
  );
}

interface SchedulePublicationPanelProps {
  t: T;
  scheduleEnabled: boolean;
  setScheduleEnabled: (v: boolean) => void;
  scheduledAt: string;
  setScheduledAt: (v: string) => void;
}

/**
 * Toggle and datetime input for scheduling a portal publication.
 */
export function SchedulePublicationPanel({
  t, scheduleEnabled, setScheduleEnabled, scheduledAt, setScheduledAt,
}: SchedulePublicationPanelProps) {
  return (
    <div className="space-y-(--space-2)">
      <Text as="label" className="flex items-center gap-(--space-2) cursor-pointer">
        <NativeCheckbox
          checked={scheduleEnabled}
          onChange={() => setScheduleEnabled(!scheduleEnabled)}
          className="w-3.5 h-3.5 accent-[var(--seed-base)]"
          aria-label={t('publish.scheduleToggle')}
          data-testid="schedule-toggle"
        />
        <Text as="span" className="text-xs font-medium text-[var(--text-primary)]">
          {t('publish.scheduleToggle')}
        </Text>
      </Text>
      {scheduleEnabled && (
        <div className="pl-(--space-5) space-y-(--space-1)">
          <Text as="span" className="text-xs text-[var(--text-tertiary)]">
            {t('publish.scheduledAt')}
          </Text>
          <NativeInput
            type="datetime-local"
            value={scheduledAt}
            min={scheduleDatetimeLocalMin()}
            onChange={(e) => setScheduledAt(e.target.value)}
            data-testid="schedule-datetime-input"
            className="w-full px-(--space-2) py-(--space-1) text-xs rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-primary)] text-[var(--text-primary)]"
          />
        </div>
      )}
    </div>
  );
}
